// Minimal stroke-icon set. Every icon is decorative (aria-hidden) and must
// always be paired with visible text or an aria-label on its parent control.
import type { SVGProps } from 'react';

const base = (props: SVGProps<SVGSVGElement>) => ({
  viewBox: '0 0 24 24',
  // Without an explicit intrinsic size, a bare <svg> falls back to the
  // browser default (~300x150) wherever no CSS rule happens to constrain
  // it — e.g. inside a plain <h2> — rendering as a huge, layout-breaking
  // icon. 24px is just the fallback; any CSS rule (`.bottom-nav svg`,
  // `.action-btn svg`, etc.) or a `width`/`height` prop still overrides it
  // via the `...props` spread below.
  width: 24,
  height: 24,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...props,
});

export const HomeIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M3 11.5 12 4l9 7.5" />
    <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
  </svg>
);

export const PlacesIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21Z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

export const FlightsIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)} fill="currentColor" stroke="none">
    <path d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2.5 1.5V22l4-1 4 1v-1.5L13 19v-5.5z" />
  </svg>
);

export const BabcockIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="8" r="4" />
    <path d="M12 12v9M8 21h8" />
  </svg>
);

export const MapIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />
  </svg>
);

export const ServicesIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 1 5.4-5.4l-2.6 2.6" />
  </svg>
);

export const EventsIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

export const MoreIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

export const SearchIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const PhoneIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M6.6 10.8a15.7 15.7 0 0 0 6.6 6.6l2.2-2.2a1.5 1.5 0 0 1 1.5-.4 11 11 0 0 0 3.5.6 1.5 1.5 0 0 1 1.5 1.5v3.4A1.5 1.5 0 0 1 20.4 22 17.9 17.9 0 0 1 2 3.6 1.5 1.5 0 0 1 3.5 2h3.4A1.5 1.5 0 0 1 8.4 3.5a11 11 0 0 0 .6 3.5 1.5 1.5 0 0 1-.4 1.5Z" />
  </svg>
);

export const GlobeIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
  </svg>
);

// Apple Maps: stylized road-map tile with a compass rose. Kept as an
// outline stroke icon in the current ink color so it reads as a system
// action, distinct from the branded Google Maps and Waze glyphs below.
export const AppleMapsIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="m9 4 6-1 6 1v16l-6-1-6 1-6-1V5Z" />
    <path d="M9 4v16M15 3v16" />
  </svg>
);

// Directions glyphs.
//
// These were previously hand-drawn approximations of the Google Maps and
// Waze app icons, using their brand colors (#EA4335, #4285F4, #34A853,
// #FBBC04, #33CCFF). Two problems with that:
//
//   1. An approximation of someone else's logo is invented branding. The
//      shapes were not the real marks and the Waze one had a drawn-on
//      face, so it misrepresented both brands.
//   2. It injected five off-palette colors into a single-accent design,
//      three times per row across 87 rows, which was a large part of why
//      the list read as busy.
//
// They are now neutral wayfinding glyphs in currentColor, differentiated
// by shape rather than by brand color: a map pin for Google Maps, a
// navigation arrow for Waze. Which service each opens is carried by the
// button's aria-label ("Directions to X in Google Maps"), so nothing is
// lost for screen readers or for sighted users, who get a tooltip.
export const GoogleMapsIcon = ({ width = 24, height = 24, ...p }: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width={width}
    height={height}
    aria-hidden
    {...p}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 21.5c4-4.6 6.5-7.6 6.5-11a6.5 6.5 0 1 0-13 0c0 3.4 2.5 6.4 6.5 11Z" />
    <circle cx="12" cy="10.2" r="2.4" />
  </svg>
);

export const WazeIcon = ({ width = 24, height = 24, ...p }: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width={width}
    height={height}
    aria-hidden
    {...p}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 3 10.5 21l-1.7-7.8L1 11.5 21 3Z" />
  </svg>
);

export const HeartIcon = ({ filled, ...p }: SVGProps<SVGSVGElement> & { filled?: boolean }) => (
  <svg {...base(p)} fill={filled ? 'currentColor' : 'none'}>
    <path d="M12 20s-7.5-4.8-9.5-9.3C1.2 7.2 3.3 4 6.8 4c2 0 3.4 1.2 5.2 3 1.8-1.8 3.2-3 5.2-3 3.5 0 5.6 3.2 4.3 6.7C19.5 15.2 12 20 12 20Z" />
  </svg>
);

export const CheckIcon = ({ checked, ...p }: SVGProps<SVGSVGElement> & { checked?: boolean }) => (
  <svg {...base(p)}>
    <rect x="4" y="4" width="16" height="16" rx="3" fill={checked ? 'currentColor' : 'none'} />
    {checked ? <path d="m7.5 12 3 3 6-6" stroke="var(--color-surface)" strokeWidth="2" /> : null}
  </svg>
);

export const ForMeIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <rect x="4" y="4" width="16" height="16" rx="3" />
    <path d="m8 12 2.5 2.5L16 9" />
  </svg>
);

export const NoteIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M15 3v5h5M8 12h8M8 16h5" />
  </svg>
);

export const UploadIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 16V4M7 8l5-5 5 5" />
    <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </svg>
);

export const DownloadIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 4v12M7 12l5 5 5-5" />
    <path d="M4 19h16" />
  </svg>
);

export const DocumentIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M15 3v5h5" />
  </svg>
);

export const AlertIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 2 1 21h22Z" />
    <path d="M12 9v5M12 18h.01" />
  </svg>
);

export const SunIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="4.5" />
    <path d="M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  </svg>
);

export const CalendarIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" />
  </svg>
);

export const PlaneStatusIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </svg>
);

export const ContactIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

export const EmergencyIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 2 3 6v6c0 5 3.8 8.7 9 10 5.2-1.3 9-5 9-10V6Z" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);

export const HouseIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M3 11.5 12 4l9 7.5" />
    <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
  </svg>
);

// Two overlapping houses — a neighborhood, not a single home.
export const NeighborhoodsIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M2.5 12 8 6.5 13.5 12" />
    <path d="M4.5 10.5V19a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1v-8.5" />
    <path d="M13.5 10.8 18.3 6.3l4.7 4.5" />
    <path d="M15.3 9.4V19a1 1 0 0 0 1 1h3.7" />
  </svg>
);

export const ExploreIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="m15 9-2 6-6 2 2-6Z" />
  </svg>
);

export const SettingsIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
  </svg>
);

export const AdminIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 2 4 5v6c0 5 3.4 8.8 8 11 4.6-2.2 8-6 8-11V5Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export const ChevronRightIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="m9 6 6 6-6 6" />
  </svg>
);

export const CloseIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

// Menu (as in a restaurant menu card) — a sheet with ruled lines.
export const MenuCardIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M9 8h6M9 12h6M9 16h3" />
  </svg>
);

// ---------------------------------------------------------------------------
// Category glyphs for generated place marks.
//
// Used as a watermark behind a place's initials in <PlaceAvatar>, so they
// are read at roughly 40-56px and must stay legible with very few strokes.
// Deliberately thinner (1.5) than the UI icon set above, because they sit
// under type rather than beside it and should not compete with it.
//
// All twelve categories in the database have one, so no place ever falls
// through to a generic shape.
// ---------------------------------------------------------------------------

const glyph = (props: SVGProps<SVGSVGElement>) => ({
  viewBox: '0 0 24 24',
  width: 24,
  height: 24,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...props,
});

/** food — fork and knife */
export const CatFoodIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M7 3v7a2 2 0 0 0 4 0V3M9 12v9" />
    <path d="M16 3c-1.5 1.2-2 2.6-2 4.2 0 1.4.7 2.3 2 2.8v11" />
  </svg>
);

/** grocery — shopping basket */
export const CatGroceryIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M3 9h18l-1.7 9.2a2 2 0 0 1-2 1.8H6.7a2 2 0 0 1-2-1.8Z" />
    <path d="M8.5 9 11 3.5M15.5 9 13 3.5M9.5 13v3M14.5 13v3" />
  </svg>
);

/** medical — cross */
export const CatMedicalIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M9.6 3h4.8v6.6H21v4.8h-6.6V21H9.6v-6.6H3V9.6h6.6Z" />
  </svg>
);

/** services — wrench */
export const CatServicesIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M15.2 6.8a3.8 3.8 0 0 1 5.1-3.6l-2.6 2.6 1.9 1.9 2.6-2.6a3.8 3.8 0 0 1-5.1 5.1L5.6 21.7a2 2 0 0 1-2.8-2.8L14.4 7.3a3.8 3.8 0 0 1 .8-.5Z" />
  </svg>
);

/** shopping — shopping bag */
export const CatShoppingIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M5 8h14l1 12.2a1.6 1.6 0 0 1-1.6 1.8H5.6A1.6 1.6 0 0 1 4 20.2Z" />
    <path d="M8.5 8V6a3.5 3.5 0 0 1 7 0v2" />
  </svg>
);

/** explore — compass */
export const CatExploreIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="m15.6 8.4-2 5.2-5.2 2 2-5.2Z" />
  </svg>
);

/** gas — fuel pump */
export const CatGasIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M5 21V5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v16" />
    <path d="M4 21h11M6.5 7.5h6v4h-6z" />
    <path d="M14 9h3.2a2 2 0 0 1 2 2v6.2a1.8 1.8 0 1 0 3.6 0V12l-2-3" />
  </svg>
);

/** recreation — trail waypoint */
export const CatRecreationIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M4 20c3-1.2 4.5-3.4 4.5-6.6C8.5 10 7 8.4 7 6.2A2.8 2.8 0 0 1 12 4.4c1.2 1.6.8 3.6-.6 5.2-1.6 1.8-2 3.4-1.2 5 .9 1.8 2.9 2.6 5.8 2.4" />
    <circle cx="18.5" cy="17.4" r="2.1" />
  </svg>
);

/** attraction — binoculars */
export const CatAttractionIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <circle cx="6.5" cy="15" r="3.6" />
    <circle cx="17.5" cy="15" r="3.6" />
    <path d="M10.1 15h3.8M7.4 11.5 8.8 5.2A1.4 1.4 0 0 1 10.2 4h.8M16.6 11.5 15.2 5.2A1.4 1.4 0 0 0 13.8 4H13" />
  </svg>
);

/** travel — aeroplane */
export const CatTravelIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M10.5 3.2a1.5 1.5 0 0 1 3 0V9l7.5 4.3v2.4l-7.5-2.2v3.9l2.6 2v1.9L12 20.2l-4.1 1.1v-1.9l2.6-2v-3.9L3 15.7v-2.4L10.5 9Z" />
  </svg>
);

/** babcock_ranch — the town: solar array over a roofline */
export const CatTownIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M3 10.5 12 4l9 6.5" />
    <path d="M5.5 9.6V20h13V9.6" />
    <path d="M9 20v-5.5h6V20M3.5 13.2h17" />
  </svg>
);

/** babcock_national — golf flag */
export const CatGolfIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M8 21V3l9.5 3.6L8 10.2" />
    <path d="M5 21h7" />
    <circle cx="17" cy="18.5" r="1.6" />
  </svg>
);

/** fitness — dumbbell */
export const CatFitnessIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M3 9v6M6.5 6.5v11M17.5 6.5v11M21 9v6M6.5 12h11" />
  </svg>
);

/** home_improvement — hammer */
export const CatHardwareIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...glyph(p)}>
    <path d="M17 3l4 4-5.5 5.5-4-4Z" />
    <path d="M12.3 8.7 4.9 16a2 2 0 0 0 0 2.8l.3.3a2 2 0 0 0 2.8 0l7.3-7.4" />
  </svg>
);
